import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableMultiSelect } from '@/components/ui/searchable-multiselect'
import { useAuthStore } from '@/store/auth.store'
import { leavePolicyService, type LeavePolicySettingDto } from '@/services/leavePolicyService'
import { showSaveSuccess } from '@/lib/swal'

const OFF_DAY_VALUES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const

const OFF_DAY_I18N_KEYS: Record<(typeof OFF_DAY_VALUES)[number], string> = {
  monday: 'leavePolicy.dayMonday',
  tuesday: 'leavePolicy.dayTuesday',
  wednesday: 'leavePolicy.dayWednesday',
  thursday: 'leavePolicy.dayThursday',
  friday: 'leavePolicy.dayFriday',
  saturday: 'leavePolicy.daySaturday',
  sunday: 'leavePolicy.daySunday',
}

function offDayKeysFromDto(d: LeavePolicySettingDto): string[] {
  const keys: string[] = []
  if (d.mondayOff) keys.push('monday')
  if (d.tuesdayOff) keys.push('tuesday')
  if (d.wednesdayOff) keys.push('wednesday')
  if (d.thursdayOff) keys.push('thursday')
  if (d.fridayOff) keys.push('friday')
  if (d.saturdayOff) keys.push('saturday')
  if (d.sundayOff) keys.push('sunday')
  return keys
}

function offDayBooleansFromKeys(selected: string[]) {
  const s = new Set(selected)
  return {
    mondayOff: s.has('monday'),
    tuesdayOff: s.has('tuesday'),
    wednesdayOff: s.has('wednesday'),
    thursdayOff: s.has('thursday'),
    fridayOff: s.has('friday'),
    saturdayOff: s.has('saturday'),
    sundayOff: s.has('sunday'),
  }
}

export type LeavePolicyForm = {
  annualLeaveDays: number
  sickLeave: number
  casualLeaves: number
  branchId: number
  offDayKeys: string[]
}

const emptyForm = (branchId: number): LeavePolicyForm => ({
  annualLeaveDays: 0,
  sickLeave: 0,
  casualLeaves: 0,
  branchId,
  offDayKeys: [],
})

export const LeavePolicy: React.FC = () => {
  const { t } = useTranslation('common')
  const branchId = useAuthStore((s) => s.user?.selectedBranch?.branchId ?? 0)

  const [saving, setSaving] = useState(false)
  const [loadingPolicy, setLoadingPolicy] = useState(false)
  const [form, setForm] = useState<LeavePolicyForm>(emptyForm(branchId))

  const normalizedForm = useMemo(() => ({ ...form, branchId }), [form, branchId])

  const accrualRate = useMemo(() => {
    const annual = Number(normalizedForm.annualLeaveDays || 0)
    return annual / 12
  }, [normalizedForm.annualLeaveDays])

  const offDayOptions = useMemo(
    () => OFF_DAY_VALUES.map((v) => ({ value: v, label: t(OFF_DAY_I18N_KEYS[v]) })),
    [t]
  )

  const validate = (value: LeavePolicyForm): string | null => {
    if (!value.branchId) return 'Please select a branch first'
    if (value.annualLeaveDays < 0) return 'Annual leave days must be 0 or greater'
    if (value.sickLeave < 0) return 'Sick leave must be 0 or greater'
    if (value.casualLeaves < 0) return 'Casual leaves must be 0 or greater'
    return null
  }

  useEffect(() => {
    if (!branchId) return
    let cancelled = false

    void (async () => {
      try {
        setLoadingPolicy(true)
        const dto = await leavePolicyService.getByBranchId(branchId)
        if (cancelled) return

        const next = dto
          ? {
              annualLeaveDays: Number(dto.annualLeave || 0),
              sickLeave: Number(dto.sickLeave || 0),
              casualLeaves: Number(dto.casualLeaves || 0),
              branchId,
              offDayKeys: offDayKeysFromDto(dto),
            }
          : emptyForm(branchId)

        setForm(next)
      } catch (e: any) {
        if (cancelled) return
        setForm(emptyForm(branchId))
        toast.error(e?.response?.data?.message || e?.message || 'Failed to load leave policy')
      } finally {
        if (!cancelled) setLoadingPolicy(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [branchId])

  const handleSave = async () => {
    const err = validate(normalizedForm)
    if (err) return toast.error(err)

    try {
      setSaving(true)
      const res: any = await leavePolicyService.saveOrUpdate({
        annualLeave: normalizedForm.annualLeaveDays,
        leaveAccrualRate: accrualRate,
        sickLeave: normalizedForm.sickLeave,
        casualLeaves: normalizedForm.casualLeaves,
        branchId: normalizedForm.branchId,
        ...offDayBooleansFromKeys(normalizedForm.offDayKeys),
      })

      const msgRaw =
        res?.result?.msg ??
        res?.result?.Msg ??
        res?.Result?.msg ??
        res?.Result?.Msg ??
        res?.message ??
        res?.Message

      const msg = typeof msgRaw === 'string' ? msgRaw.trim() : ''
      const isEditMode = /\b(update|updated|edit|edited)\b/i.test(msg)
      await showSaveSuccess(isEditMode, msg || 'Leave policy saved')
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to save leave policy')
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('leavePolicy.title')} description={t('leavePolicy.description')} />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('leavePolicy.title')} description={t('leavePolicy.description')} />

      <Card>
        <CardHeader className="bg-gradient-to-r from-primary/10 to-secondary/10 border-b">
          <CardTitle className="text-lg font-semibold">{t('leavePolicy.title')}</CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>{t('leavePolicy.annualLeaveDays')}</Label>
              <Input
                type="number"
                min={0}
                value={normalizedForm.annualLeaveDays}
                onChange={(e) => setForm((p) => ({ ...p, annualLeaveDays: Number(e.target.value || 0) }))}
                disabled={saving || loadingPolicy}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('leavePolicy.accrualRate')}</Label>
              <Input value={Number.isFinite(accrualRate) ? accrualRate.toFixed(4) : '0.0000'} readOnly className="bg-muted" />
            </div>

            <div className="space-y-2">
              <Label>{t('leavePolicy.sickLeave')}</Label>
              <Input
                type="number"
                min={0}
                value={normalizedForm.sickLeave}
                onChange={(e) => setForm((p) => ({ ...p, sickLeave: Number(e.target.value || 0) }))}
                disabled={saving || loadingPolicy}
              />
            </div>

            <div className="space-y-2">
              <Label>{t('leavePolicy.casualLeaves')}</Label>
              <Input
                type="number"
                min={0}
                value={normalizedForm.casualLeaves}
                onChange={(e) => setForm((p) => ({ ...p, casualLeaves: Number(e.target.value || 0) }))}
                disabled={saving || loadingPolicy}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>{t('leavePolicy.offDays')}</Label>
              <SearchableMultiSelect
                options={offDayOptions}
                value={normalizedForm.offDayKeys}
                onValueChange={(v) => setForm((p) => ({ ...p, offDayKeys: v }))}
                placeholder={t('leavePolicy.offDaysPlaceholder')}
                searchPlaceholder={t('leavePolicy.offDaysSearch')}
                disabled={saving || loadingPolicy}
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-2">
            <Button type="button" onClick={handleSave} disabled={saving || loadingPolicy}>
              <Save className="mr-2 h-4 w-4" />
              {t('leavePolicy.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

